import { motion } from 'framer-motion';
import { useState } from 'react';

const CoreStrengths = () => {
  const [hoveredStrength, setHoveredStrength] = useState<number | null>(null);
  const [clickedStrength, setClickedStrength] = useState<number | null>(null);

  const strengths = [
    {
      title: "FIXER OF ALL THINGS",
      subtitle: "CHAOS TAMER",
      description: "20+ years of experience doing what I love most- crafting and building things and solving problems. Even in elementary school, teachers were asking me to help them figure out their tech."
    },
    {
      title: "CREATOR",
      subtitle: "AT HEART", 
      description: "While I wait for the brief, I'll start building. You should see the ideas for this site in the first iteration. It took time to iron it out, but I got feedback, made adjustments and new ideas sparked after building one thing that led to another, etc."
    },
    {
      title: "NARRATIVE-DRIVEN",
      subtitle: "EVERYTHING",
      description: "My lifelong drive has always been editing and post production- with an affinity for still being on set when I can. Telling stories while handling the workflow and technical organization side of things made so much sense and fit everything about my personality."
    },
    {
      title: "BIG VIBES",
      subtitle: "AI FANATIC",
      description: "Vibe coding has really taken off for me and I've been using it to build things like this site, and I'm always looking for new ways to use it to help people...or just for fun and no one will ever see it."
    },
    {
      title: "EVERY HAT",
      subtitle: "WORN-WELL",
      description: "Dropped out of school to co-found a company and never looked back. I've played every role: sales, creative, ops, accounting (sadly), dev, producer, account, and built teams that could trust me to do it right or figure it out fast."
    },
    {
      title: "SERIAL",
      subtitle: "SIDE-QUESTER",
      description: "Whether it's experimenting with 3D animation, learning game dev, exploring medium format photography, or spinning up new platforms for fun—I thrive on curiosity. If it feels like a creative rabbit hole, I'm probably already down it."
    }
  ];

  const handleStrengthClick = (index: number) => {
    setClickedStrength(index);
    // Reset the clicked state after animation
    setTimeout(() => setClickedStrength(null), 600);
  };

  return (
    <div className="h-screen bg-transparent text-white overflow-hidden md:overflow-hidden overflow-y-auto">
      {/* Split Screen Layout */}
      <div className="flex flex-col md:flex-row h-full px-4 sm:px-6 lg:px-8 py-4 pb-24 md:pb-4">
        {/* Left Side - Strength List */}
        <div className="w-full md:w-1/2 md:pr-4 lg:pr-8 flex flex-col justify-center md:translate-y-[-5vh] pl-4 lg:pl-20 pt-4 md:pt-0">
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 'clamp(0.25rem, 0.6vh, 1rem)',
            height: 'fit-content',
            maxHeight: '75vh',
            justifyContent: 'center'
          }}>
            {strengths.map((strength, index) => (
              <motion.div
                key={index}
                className="cursor-pointer group"
                onMouseEnter={() => setHoveredStrength(index)}
                onMouseLeave={() => setHoveredStrength(null)}
                onClick={() => handleStrengthClick(index)}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {/* Brutalist Block */}
                <div className="relative">
                  {/* Background shadow block */}
                  <div className="absolute top-1 lg:top-1.5 xl:top-2 left-1 lg:left-1.5 xl:left-2 w-full h-full bg-white"></div>
                  
                  {/* Main content block */}
                  <motion.div 
                    className={`relative bg-black border-2 lg:border-3 xl:border-4 border-white p-1.5 lg:p-2.5 xl:p-4 transition-transform duration-200 ${
                      hoveredStrength === index ? 'transform -translate-x-1 -translate-y-1' : ''
                    }`}
                    animate={clickedStrength === index ? {
                      scale: [1, 1.02, 1],
                      rotate: [0, -1, 1, 0]
                    } : {}}
                    transition={{ duration: 0.6 }}
                  >
                    {/* Main Title */}
                    <h2 
                      className="font-black uppercase leading-none text-white"
                      style={{
                        fontSize: 'clamp(0.875rem, 2.2vw, 2.8rem)',
                        lineHeight: '0.9'
                      }}
                    >
                      {strength.title}
                    </h2>
                    
                    {/* Subtitle */}
                    <p 
                      className="font-bold uppercase tracking-widest text-gray-400"
                      style={{
                        fontSize: 'clamp(0.35rem, 0.5vw, 0.7rem)',
                        marginTop: 'clamp(0.1rem, 0.15vh, 0.2rem)'
                      }}
                    >
                      {strength.subtitle}
                    </p>
                    
                    {/* Accent bar - highlighted when hovered (not clicked) */}
                    <div className={`w-4 lg:w-6 xl:w-8 h-0.5 lg:h-0.5 xl:h-1 transition-colors duration-200 ${
                      hoveredStrength === index ? 'bg-white' : 'bg-white/60'
                    }`} style={{
                      marginTop: 'clamp(0.15rem, 0.2vh, 0.3rem)'
                    }}></div>
                    
                    {/* Mobile tap indicator - shows hover state */}
                    <div className="md:hidden absolute bottom-1 right-1">
                      <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                        hoveredStrength === index ? 'bg-white' : 'bg-white/30'
                      }`}></div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Side - Description Panel */}
        <div className="w-full md:w-1/2 md:pl-4 lg:pl-8 flex items-center mt-6 md:mt-0 md:translate-y-[-5vh] pr-4 lg:pr-20">
          <div className="w-full">
            {hoveredStrength !== null ? (
              <motion.div
                key={hoveredStrength}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.4 }}
                className="relative"
              >
                {/* Brutalist description block */}
                <div className="relative">
                  {/* Background shadow */}
                  <div className="absolute top-1.5 lg:top-3 xl:top-4 left-1.5 lg:left-3 xl:left-4 w-full h-full bg-white/20"></div>
                  
                  {/* Main content */}
                  <div className="relative bg-black border-2 lg:border-3 xl:border-4 border-white p-3 lg:p-4 xl:p-6">
                    {/* Header bar */}
                    <div className="bg-white text-black p-1.5 lg:p-2 xl:p-3 mb-2 lg:mb-3 xl:mb-4 border-1 lg:border-1.5 xl:border-2 border-white transform rotate-1">
                      <h3 
                        className="font-black uppercase tracking-wider"
                        style={{
                          fontSize: 'clamp(0.5rem, 0.8vw, 0.9rem)'
                        }}
                      >
                        {strengths[hoveredStrength].title}
                      </h3>
                    </div>
                    
                    {/* Description */}
                    <p 
                      className="text-white leading-relaxed font-medium"
                      style={{
                        fontSize: 'clamp(0.675rem, 1vw, 1.1rem)'
                      }}
                    >
                      {strengths[hoveredStrength].description}
                    </p>
                    
                    {/* Bottom accent */}
                    <div className="flex gap-1 lg:gap-1.5 xl:gap-2 mt-2 lg:mt-3 xl:mt-4">
                      <div className="w-1.5 lg:w-3 xl:w-4 h-1.5 lg:h-3 xl:h-4 bg-white"></div>
                      <div className="w-1.5 lg:w-3 xl:w-4 h-1.5 lg:h-3 xl:h-4 bg-white/60"></div>
                      <div className="w-1.5 lg:w-3 xl:w-4 h-1.5 lg:h-3 xl:h-4 bg-white/30"></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="relative"
              >
                {/* Default brutalist block */}
                <div className="relative">
                  <div className="absolute top-1.5 lg:top-3 xl:top-4 left-1.5 lg:left-3 xl:left-4 w-full h-full bg-white/10"></div>
                  <div className="relative bg-black border-2 lg:border-3 xl:border-4 border-white p-4 lg:p-6 xl:p-8 text-center">
                    <div className="bg-white/10 border-1 lg:border-1.5 xl:border-2 border-dashed border-white/50 p-3 lg:p-4 xl:p-6">
                      <div className="w-5 lg:w-6 xl:w-8 h-5 lg:h-6 xl:h-8 bg-white mx-auto mb-2 lg:mb-3 xl:mb-4 transform rotate-45"></div>
                      <p 
                        className="font-black uppercase tracking-widest text-white"
                        style={{
                          fontSize: 'clamp(0.5rem, 0.8vw, 0.9rem)'
                        }}
                      >
                        <span className="hidden md:inline"></span>
                        <span className="md:hidden"></span>
                      </p>
                      <div className="w-8 lg:w-12 xl:w-16 h-0.5 lg:h-0.5 xl:h-1 bg-white mx-auto mt-1.5 lg:mt-2 xl:mt-4"></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoreStrengths; 